import { useDispatch, useSelector } from "react-redux";
import Button from "@/common/elements/Button";
import {
  StyledFlex,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import { BackwardOutlined, SaveOutlined } from "@ant-design/icons";
import { Form, Spin, Modal } from "antd";
import StateSubmissionInformation from "./StateSubmissionInformation";
import WarrantInformations from "./WarrantInformations";
import OffenseInformationDetails from "./OffenseInformationDetails";
import WarrantCourtInformation from "./WarrantCourtInformation";
import WarrantCaseInformation from "./WarrantCaseInformation";
import WarrantBondInformation from "./WarrantBondInformation";
import AdditionalWarrantInformation from "./AdditionalWarrantInformation";
import ExtraditionInformation from "./ExtraditionInformation";
import WarrantComments from "./WarrantComments";
import InterestedParties from "./InterestedParties";
import ServiceAttemptDetails from "./ServiceAttemptDetails";
import CashAccountDetails from "./CashAccountDetails";
import LinkedScannedDocuments from "@/components/BookingsDetails/LinkedScannedDocuments";
import WarrantAdministrativeInformation from "./WarrantAdministrativeInformation";
import LocateInformationForm from "./LocateInformationForm";
import { forwardRef, useImperativeHandle, useState } from "react";
import {
  getRefListNameByID,
  isNonEmptyVariable,
  getOptionsFromRefList,
} from "@/util/common_function";
import notificationService from "@/util/notificationService";
import useDialog from "@/hooks/useDialog";
import useActiveWarrant from "@/hooks/useActiveWarrant";

const WarrantFullDetails = forwardRef(
  ({ mode = "add", onBack, onSaved }, ref) => {
    const [isDoneProcessing, setIsDoneProcessing] = useState(false);
    const [warrantForm] = Form.useForm();
    const { activeWarrant } = useSelector((s) => s?.warrants ?? {});
    const { bookings, bookingsFieldsEnabled } = useSelector((s) => s?.bookings);
    const { patchArray } = useActiveWarrant();

    const [isLocateInfoState, setIsLocateInfoState] = useState(false);

    const handleLocalInfoState = (val) => setIsLocateInfoState(val);

    const { showDialog } = useDialog();

    const showConfirm = (options) =>
      new Promise((resolve) => {
        Modal.confirm({
          title: options.title,
          content: options.content,
          okText: options.confirmButtonText ?? "Yes",
          cancelText: options.cancelButtonText ?? "No",
          centered: true,
          maskClosable: false,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

    const onHandleBack = () => {
      if (typeof onBack === "function") onBack(true);
    };

    useImperativeHandle(ref, () => ({
      triggerDone: handleDone,
    }));

    const focusFirstErrorField = async (errorInfo) => {
      const name = errorInfo?.errorFields?.[0]?.name;
      if (!name) return;

      // Wait a tick for any conditional sections (Wait For / Clearance / Locate Info)
      // to mount before we try to scroll to a field inside them.
      await new Promise((resolve) => setTimeout(resolve, 50));

      warrantForm.scrollToField(name, { behavior: "smooth", block: "center" });
      warrantForm.getFieldInstance(name)?.focus?.();
    };

    const handleDone = async () => {
      if (!isNonEmptyVariable(activeWarrant)) {
        notificationService.error("No warrant data to save");
        return false;
      }

      try {
        await warrantForm.validateFields();
      } catch (errorInfo) {
        const statusId = activeWarrant?.statusId;
        const statusOptions = getOptionsFromRefList(
          "Status",
          false,
          false,
          true,
        );
        const statusLabel =
          statusOptions.find((o) => o.value === statusId)?.label ?? "";

        const isWaitFor = ["WAIT FOR BODY", "WAIT FOR $"].includes(statusLabel);
        const isCleared = ["CLEARED"].includes(statusLabel);

        if (isWaitFor) {
          notificationService.error(
            "Fill out all mandatory fields: Wait For Reason, Wait For Date/Time, and Cleared By are required.",
          );
        } else if (isCleared) {
          notificationService.error(
            "Fill out all mandatory fields: Clearance Reason, Clearance Date/Time, and Cleared By are required.",
          );
        } else {
          notificationService.error("Fill out all mandatory fields.");
        }
        await focusFirstErrorField(errorInfo);
        return false;
      }

      // ─── Charge validation ───────────────────────────────────────────
      const charges = activeWarrant?.warrantCharges ?? [];
      if (charges.length === 0) {
        showDialog("alert", {
          title: "Validation Error",
          content: `You must enter one charge.`,
        });
        return false;
      }
      // ─────────────────────────────────────────────────────────────────

      // ─── Arresting officer vs status check ──────────────────────────────────
      const arrestingOfficer = activeWarrant?.arrestingOfficerStaffId;

      if (arrestingOfficer) {
        const statusOptions = getOptionsFromRefList(
          "Status",
          false,
          false,
          true,
        );
        const clearedOption = statusOptions.find(
          (o) => o.label?.toUpperCase() === "CLEARED",
        );

        if (activeWarrant?.statusId !== clearedOption?.value) {
          showDialog("alert", {
            title: "Validation Error",
            content:
              "You have selected an officer from Arresting officer field, but the status of the warrant is not CLEARED.",
            confirmButtonText: "Ok",
          });
          return false;
        }
      }
      // ─────────────────────────────────────────────────────────────────────────

      // ─── Active warrant state conflict check (only when adding) ──────────
      const existingWarrants = bookings?.warrants ?? [];

      const currentMakeActive =
        activeWarrant?.warrantStateInfo?.[0]?.instructionMakeActiveAtState ===
        true;

      const conflictingWarrant = currentMakeActive
        ? existingWarrants.find(
            (w) =>
              w?.warrantStateInfo?.[0]?.instructionMakeActiveAtState === true &&
              w?.warrantId !== activeWarrant?.warrantId,
          )
        : null;

      if (conflictingWarrant) {
        const warrantNumber = conflictingWarrant?.warrantNumber ?? "N/A";
        const dateIssued = conflictingWarrant?.dateIssued ?? "N/A";
        const warrantStatus =
          getRefListNameByID("Status", conflictingWarrant?.statusId) || "N/A";

        // const confirmed = await showConfirm("confirm", {
        //   title: "Active Warrant Conflict",
        //   content:
        //     `Only one warrant can be marked as Active for this person. ` +
        //     `If you wish to continue we will clear the following warrant at the state and then send the current warrant.\n\n` +
        //     `Warrant #${warrantNumber} was issued on ${dateIssued}. ` +
        //     `This status is ${warrantStatus}. Do you want to continue?`,
        // });
        const confirmed = await new Promise((resolve) => {
          Modal.confirm({
            title: "Active Warrant Conflict",
            content:
              `Only one warrant can be marked as Active for this person. ` +
              `If you wish to continue we will clear the following warrant at the state and then send the current warrant.\n\n` +
              `Warrant #${warrantNumber} was issued on ${dateIssued}. ` +
              `This status is ${warrantStatus}. Do you want to continue?`,
            okText: "Yes",
            cancelText: "No",
            centered: true,
            maskClosable: false,
            width: 500,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        // if (confirmed) {
        //   // YES — tell me what to do here
        //   return true;
        // } else {
        //   // NO — abort
        //   return true;
        // }
      }
      // ─────────────────────────────────────────────────────────────────────

      const normalizeTime = (val) => {
        if (!val) return null;
        if (Array.isArray(val) && val.length >= 2) {
          return `${String(val[0]).padStart(2, "0")}:${String(val[1]).padStart(2, "0")}`;
        }
        return val; // already a string
      };

      console.log("...activeWarrant ", activeWarrant);

      // Build the warrant object to merge back — ensure all sub-arrays are present
      const warrantToSave = {
        ...activeWarrant,
        offenseTime: normalizeTime(activeWarrant?.offenseTime),
        jacketId: activeWarrant?.jacketId ?? bookings?.jacketId,
        warrantCharges: activeWarrant?.warrantCharges ?? [],
        warrantStateInfo: activeWarrant?.warrantStateInfo ?? [],
        warrantExtraditionInfo: activeWarrant?.warrantExtraditionInfo ?? [],
        warrantVictim: activeWarrant?.warrantVictim ?? [],
        warrantCashAccounts: activeWarrant?.warrantCashAccounts ?? [],
        warrantAffiant: activeWarrant?.warrantAffiant ?? [],
        warrantLinkedDocuments: activeWarrant?.warrantLinkedDocuments ?? [],
        serviceDocuments: activeWarrant?.serviceDocuments ?? [],
      };

      console.log("warrantToSave ", warrantToSave);

      // ─── Soft validation — incomplete fields → offer to mark as Pending ──────────
      const missingFields = [];

      // StateSubmissionInformation
      const stateInfo = activeWarrant?.warrantStateInfo?.[0] ?? {};
      if (!isNonEmptyVariable(stateInfo?.miscellaneousField)) {
        missingFields.push("Miscellaneous Field (State Submission)");
      }
      if (
        activeWarrant?.useAlias &&
        !isNonEmptyVariable(activeWarrant?.bioAliasId)
      ) {
        missingFields.push("Alias Name (State Submission)");
      }

      // WarrantInformations
      if (!isNonEmptyVariable(activeWarrant?.warrantNumber)) {
        missingFields.push("Warrant #");
      }
      if (!isNonEmptyVariable(activeWarrant?.dateIssued)) {
        missingFields.push("Issued Date");
      }
      if (activeWarrant?.juvenileWarrant) {
        if (!isNonEmptyVariable(activeWarrant?.dateOfEmancipation)) {
          missingFields.push("Date of Emancipation");
        }
        if (!isNonEmptyVariable(activeWarrant?.dateOfViolation)) {
          missingFields.push("Date of Violation");
        }
      }

      // WarrantCourtInformation
      if (!isNonEmptyVariable(activeWarrant?.courtNameId)) {
        missingFields.push("Court");
      }

      // WarrantCaseInformation
      if (!isNonEmptyVariable(activeWarrant?.ori)) {
        missingFields.push("ORI");
      }
      if (!isNonEmptyVariable(activeWarrant?.crnNo)) {
        missingFields.push("CRN");
      }

      // WarrantBondInformation
      if (!isNonEmptyVariable(activeWarrant?.bondAmount)) {
        missingFields.push("Bond Amount");
      }

      // ExtraditionInformation
      const extradition = activeWarrant?.warrantExtraditionInfo?.[0];
      if (!isNonEmptyVariable(extradition?.extraditionAreaID)) {
        missingFields.push("Extradition Area");
      }

      if (missingFields.length > 0) {
        console.log("Inside missingFields");
        const confirmed = await new Promise((resolve) => {
          Modal.confirm({
            title: "Incomplete Fields",
            content: `Would you like to save this warrant anyway, even though it is not yet ready to save? If you answer Yes, then this warrant will be put into Pending state and will not be submitted until you resolve all issues with it.`,
            okText: "Yes",
            cancelText: "No",
            centered: true,
            maskClosable: false,
            width: 500,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        if (confirmed) {
          // Mark as pending and fall through to save
          warrantToSave.pending = true; // will be defined below — so hoist the object first (see note)
          console.log("pending after set:", warrantToSave.pending);
        } else {
          warrantToSave.pending = false;
        }
        // If No — fall through and save as-is (no pending flag change)
      } else {
        warrantToSave.pending = false;
      }
      // ─────────────────────────────────────────────────────────────────────────────
      console.log("After that block");
      // Bubble up to WarrantsListDataTable → updates bookings.warrants[] in Redux
      if (typeof onSaved === "function") onSaved(warrantToSave);
      return true;
    };

    return (
      <Spin spinning={false}>
        {/* ── Header bar ── */}
        {/* <StyledFlex gap="10px" marginBottom="15px" align="center" justify="space-between">
        <StyledFlex gap="10px" align="center">
          <Button onClick={onHandleBack} color="secondary" type="secondary">
            <BackwardOutlined style={{ fontSize: "18px" }} /> Back
          </Button>
          <h3 style={{ color: "var(--text-blue-title)", margin: 0 }}>
            {mode === "add" ? "New Warrant" : "Warrant Details"}
          </h3>
        </StyledFlex>
        <StyledFlex gap="10px">
          {bookingsFieldsEnabled && (
            <Button
              color="warning"
              variant="solid"
              onClick={handleDone}
              icon={<SaveOutlined />}
            >
              Save
            </Button>
          )}
        </StyledFlex>
      </StyledFlex> */}

        {/* ── Status Banner ── */}
        {activeWarrant?.pending === true && (
          <div
            style={{
              width: "100%",
              backgroundColor: "var(--primary-blue, #1677ff)",
              color: "#fff",
              textAlign: "center",
              padding: "8px 0",
              fontWeight: 1000,
              fontSize: "14px",
              letterSpacing: "0.5px",
              marginBottom: "12px",
              borderRadius: "4px",
            }}
          >
            PENDING
          </div>
        )}

        {/* ── All sub-forms — each reads/writes activeWarrant via useActiveWarrant ── */}
        <StateSubmissionInformation />

        <WarrantInformations
          locateInfoState={handleLocalInfoState}
          form={warrantForm}
        />

        <OffenseInformationDetails />

        <WarrantCourtInformation />

        <WarrantCaseInformation form={warrantForm} />

        <WarrantBondInformation />

        <AdditionalWarrantInformation />

        <ExtraditionInformation />

        {isLocateInfoState && <LocateInformationForm />}

        <WarrantComments />

        {/* Hidden for now per request — re-enable by uncommenting */}
        {/* <InterestedParties /> */}

        <ServiceAttemptDetails warrantForm={warrantForm} />

        <CashAccountDetails />

        <StyledSectionHeading marginTop="20px">
          <span>Linked and Scanned Documents</span>
        </StyledSectionHeading>
        <StyledSubTableContainer style={{ marginBottom: "25px" }}>
          <LinkedScannedDocuments
            documents={activeWarrant?.warrantLinkedDocuments}
            onChange={(updated) =>
              patchArray("warrantLinkedDocuments", updated)
            }
            bookingsFieldsEnabled={bookingsFieldsEnabled}
            idFieldName="warrantLinkedDocumentID"
            parentIdFieldName="warrantID"
            parentId={activeWarrant?.warrantId ?? activeWarrant?.warrantID}
          />
        </StyledSubTableContainer>

        <WarrantAdministrativeInformation />
      </Spin>
    );
  },
);

export default WarrantFullDetails;
